/**
 * Client-side image compression for faster uploads (especially on mobile).
 * Resizes to max dimension and compresses to JPEG to reduce file size.
 */

const DEFAULT_MAX_DIMENSION = 1920;
const DEFAULT_QUALITY = 0.85;

/** Presets: "default" (high quality), "fast" (smaller/faster uploads), "document" (legibility) */
export const COMPRESSION_PRESETS = {
  default: { maxDimension: 1920, quality: 0.85 },
  fast: { maxDimension: 1280, quality: 0.78 },
  document: { maxDimension: 1920, quality: 0.85 },
};

/**
 * Compress an image file: resize to max dimension and reduce quality.
 * @param {File} file - Image file (e.g. from input or camera)
 * @param {Object} options - { preset?: 'default'|'fast'|'document', maxDimension?: number, quality?: number }
 * @returns {Promise<File>} - Compressed image as JPEG File, or original file on skip/error
 */
export async function compressImage(file, options = {}) {
  if (!file || typeof file.type !== "string" || !file.type.startsWith("image/")) {
    return file;
  }

  const preset = options.preset && COMPRESSION_PRESETS[options.preset]
    ? COMPRESSION_PRESETS[options.preset]
    : null;
  const maxDimension = options.maxDimension ?? preset?.maxDimension ?? DEFAULT_MAX_DIMENSION;
  const quality = Math.min(
    1,
    Math.max(0.1, options.quality ?? preset?.quality ?? DEFAULT_QUALITY),
  );

  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;
      if (width <= maxDimension && height <= maxDimension) {
        // Only compress quality, no resize
        width = img.width;
        height = img.height;
      } else {
        if (width > height) {
          height = Math.round((height / width) * maxDimension);
          width = maxDimension;
        } else {
          width = Math.round((width / height) * maxDimension);
          height = maxDimension;
        }
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(file);
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          const baseName = file.name.replace(/\.[^.]+$/, "");
          const compressed = new File([blob], `${baseName}.jpg`, {
            type: "image/jpeg",
            lastModified: Date.now(),
          });
          resolve(compressed);
        },
        "image/jpeg",
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };

    img.src = url;
  });
}

/**
 * Compress multiple image files.
 * @param {File[]} files - Array of files (non-images are returned as-is)
 * @param {Object} options - Same as compressImage
 * @returns {Promise<File[]>}
 */
export async function compressImages(files, options = {}) {
  if (!Array.isArray(files) || files.length === 0) return [];
  return Promise.all(files.map((file) => compressImage(file, options)));
}
